import { useState } from "react";
import { useAppearance } from "../contexts/AppearanceContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import { Settings2, Check, DollarSign, Phone, ChevronsUpDown, BarChart2 } from "lucide-react";
import { COUNTRIES, applyMask } from "../components/PhoneInput";

function SearchCombobox({ value, onSelect, options, placeholder = "Selecionar...", searchPlaceholder = "Buscar...", className = "" }) {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button"
          className={`flex items-center justify-between gap-2 h-9 px-3 border border-slate-200 rounded-md bg-white hover:border-slate-300 text-sm transition-colors ${className}`}
          style={{ minWidth: 220 }}>
          <span className="truncate text-left">{selected ? selected.label : <span className="text-slate-400">{placeholder}</span>}</span>
          <ChevronsUpDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-72" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList className="max-h-60">
            <CommandEmpty>Nenhum resultado.</CommandEmpty>
            <CommandGroup>
              {options.map(opt => (
                <CommandItem key={opt.value} value={opt.searchValue || opt.label} onSelect={() => { onSelect(opt.value); setOpen(false); }}
                  className="flex items-center gap-2 cursor-pointer">
                  {opt.icon && <span className="text-base leading-none flex-shrink-0">{opt.icon}</span>}
                  <span className="flex-1 text-sm truncate">{opt.label}</span>
                  {opt.value === value && <Check className="w-3.5 h-3.5 flex-shrink-0 text-emerald-500" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const CURRENCIES = [
  { value: "BRL",    flag: "\u{1F1E7}\u{1F1F7}", country: "Brasil",             info: "R$ \u2014 Real Brasileiro" },
  { value: "ARS",    flag: "\u{1F1E6}\u{1F1F7}", country: "Argentina",           info: "$ \u2014 Peso Argentino" },
  { value: "CLP",    flag: "\u{1F1E8}\u{1F1F1}", country: "Chile",               info: "$ \u2014 Peso Chileno" },
  { value: "COP",    flag: "\u{1F1E8}\u{1F1F4}", country: "Col\u00f4mbia",       info: "$ \u2014 Peso Colombiano" },
  { value: "PEN",    flag: "\u{1F1F5}\u{1F1EA}", country: "Peru",                info: "S/ \u2014 Sol Peruano" },
  { value: "VES",    flag: "\u{1F1FB}\u{1F1EA}", country: "Venezuela",           info: "Bs. \u2014 Bol\u00edvar Soberano" },
  { value: "USD_EC", flag: "\u{1F1EA}\u{1F1E8}", country: "Equador",             info: "$ \u2014 D\u00f3lar (uso do USD)" },
  { value: "BOB",    flag: "\u{1F1E7}\u{1F1F4}", country: "Bol\u00edvia",        info: "Bs \u2014 Boliviano" },
  { value: "PYG",    flag: "\u{1F1F5}\u{1F1FE}", country: "Paraguai",            info: "\u20b2 \u2014 Guarani" },
  { value: "UYU",    flag: "\u{1F1FA}\u{1F1FE}", country: "Uruguai",             info: "$U \u2014 Peso Uruguaio" },
  { value: "MXN",    flag: "\u{1F1F2}\u{1F1FD}", country: "M\u00e9xico",         info: "$ \u2014 Peso Mexicano" },
  { value: "GTQ",    flag: "\u{1F1EC}\u{1F1F9}", country: "Guatemala",           info: "Q \u2014 Quetzal" },
  { value: "CRC",    flag: "\u{1F1E8}\u{1F1F7}", country: "Costa Rica",          info: "\u20a1 \u2014 Col\u00f3n" },
  { value: "PAB",    flag: "\u{1F1F5}\u{1F1E6}", country: "Panam\u00e1",         info: "B/. \u2014 Balboa / USD" },
  { value: "HNL",    flag: "\u{1F1ED}\u{1F1F3}", country: "Honduras",            info: "L \u2014 Lempira" },
  { value: "SVC",    flag: "\u{1F1F8}\u{1F1FB}", country: "El Salvador",         info: "\u20a1 \u2014 Col\u00f3n / USD" },
  { value: "NIO",    flag: "\u{1F1F3}\u{1F1EE}", country: "Nicar\u00e1gua",      info: "C$ \u2014 C\u00f3rdoba" },
  { value: "CUP",    flag: "\u{1F1E8}\u{1F1FA}", country: "Cuba",                info: "\u20b1 \u2014 Peso Cubano" },
  { value: "DOP",    flag: "\u{1F1E9}\u{1F1F4}", country: "Rep. Dominicana",     info: "RD$ \u2014 Peso Dominicano" },
  { value: "HTG",    flag: "\u{1F1ED}\u{1F1F9}", country: "Haiti",               info: "G \u2014 Gourde" },
  { value: "TTD",    flag: "\u{1F1F9}\u{1F1F9}", country: "Trinidad e Tobago",   info: "TT$ \u2014 D\u00f3lar de Trinidad" },
  { value: "JMD",    flag: "\u{1F1EF}\u{1F1F2}", country: "Jamaica",             info: "J$ \u2014 D\u00f3lar Jamaicano" },
  { value: "USD",    flag: "\u{1F1FA}\u{1F1F8}", country: "Estados Unidos",      info: "$ \u2014 D\u00f3lar Americano" },
  { value: "CAD",    flag: "\u{1F1E8}\u{1F1E6}", country: "Canad\u00e1",         info: "C$ \u2014 D\u00f3lar Canadense" },
  { value: "EUR",    flag: "\u{1F1EA}\u{1F1FA}", country: "Zona do Euro",        info: "\u20ac \u2014 Euro" },
  { value: "GBP",    flag: "\u{1F1EC}\u{1F1E7}", country: "Reino Unido",         info: "\u00a3 \u2014 Libra Esterlina" },
  { value: "CHF",    flag: "\u{1F1E8}\u{1F1ED}", country: "Su\u00ed\u00e7a",     info: "CHF \u2014 Franco Su\u00ed\u00e7o" },
  { value: "JPY",    flag: "\u{1F1EF}\u{1F1F5}", country: "Jap\u00e3o",          info: "\u00a5 \u2014 Iene" },
  { value: "CNY",    flag: "\u{1F1E8}\u{1F1F3}", country: "China",               info: "\u00a5 \u2014 Yuan Renminbi" },
  { value: "AUD",    flag: "\u{1F1E6}\u{1F1FA}", country: "Austr\u00e1lia",      info: "A$ \u2014 D\u00f3lar Australiano" },
].map(c => ({
  ...c,
  value: c.value,
  icon: c.flag,
  label: `${c.country} (${c.info})`,
  searchValue: `${c.country} ${c.info} ${c.value} ${c.flag}`,
}));

export default function GeralPage() {
  const appearance = useAppearance();
  const { updateAppearance } = appearance;
  const tc500 = appearance.colors?.["500"] || "#3B82F6";
  const tc50 = appearance.colors?.["50"] || "#EFF6FF";

  return (
    <div data-testid="geral-page" className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}>
          <Settings2 className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight">Geral</h1>
          <p className="text-slate-500 mt-0.5">Moeda, telefone e tipo de negócio padrão do tenant.</p>
        </div>
      </div>

      {/* Currency */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-heading text-slate-900 flex items-center gap-2">
            <DollarSign className="w-5 h-5" style={{ color: tc500 }} />Moeda Padrão do Tenant
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 mb-4">Moeda utilizada em todos os negócios e relatórios deste tenant.</p>
          <SearchCombobox
            value={appearance.default_currency || "BRL"}
            onSelect={v => updateAppearance({ default_currency: v })}
            options={CURRENCIES}
            placeholder="Selecionar moeda..."
            searchPlaceholder="Buscar moeda ou país..."
          />
        </CardContent>
      </Card>

      {/* Phone Settings */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-heading text-slate-900 flex items-center gap-2">
            <Phone className="w-5 h-5" style={{ color: tc500 }} />Configurações de Telefone
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-slate-500">
            Define o DDI padrão e a máscara de exibição dos telefones para todos os usuários deste tenant.
            Use <code className="bg-slate-100 px-1 rounded text-xs font-mono">#</code> para representar cada dígito.
          </p>

          <div className="space-y-2">
            <Label className="text-slate-700">DDI / País padrão</Label>
            <div className="flex gap-2 flex-wrap">
              {COUNTRIES.slice(0, 8).concat(COUNTRIES.filter(c => !COUNTRIES.slice(0,8).includes(c) && c.code === (appearance.ddi_country || "BR"))).filter((c,i,a) => a.findIndex(x=>x.code===c.code)===i).map(c => (
                <button key={c.code} type="button"
                  onClick={() => updateAppearance({ default_ddi: c.ddi, ddi_country: c.code, phone_mask: c.mask })}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-all ${(appearance.ddi_country || "BR") === c.code ? "text-white font-semibold" : "border-slate-200 text-slate-600 hover:border-slate-300 bg-white"}`}
                  style={(appearance.ddi_country || "BR") === c.code ? { backgroundColor: tc500, borderColor: tc500 } : undefined}>
                  {c.flag} +{c.ddi} <span className="text-xs opacity-80">{c.code}</span>
                </button>
              ))}
            </div>
            <div className="flex gap-2 items-center mt-2">
              <Label className="text-xs text-slate-500 w-20 shrink-0">Outro DDI:</Label>
              <SearchCombobox
                value={appearance.ddi_country || "BR"}
                onSelect={v => {
                  const c = COUNTRIES.find(x => x.code === v);
                  if (c) updateAppearance({ default_ddi: c.ddi, ddi_country: c.code, phone_mask: c.mask });
                }}
                options={COUNTRIES.map(c => ({
                  value: c.code,
                  icon: c.flag,
                  label: `${c.name} (+${c.ddi})`,
                  searchValue: `${c.name} ${c.ddi} ${c.code} ${c.flag}`,
                }))}
                placeholder="Selecionar país..."
                searchPlaceholder="Buscar país ou DDI..."
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700">Máscara de telefone</Label>
            <div className="flex gap-3 items-center max-w-sm">
              <Input
                value={appearance.phone_mask || "(##) #####-####"}
                onChange={e => updateAppearance({ phone_mask: e.target.value })}
                className="font-mono text-sm h-9"
                placeholder="(##) #####-####"
              />
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>Pré-visualização:</span>
              <code className="bg-slate-100 px-2 py-0.5 rounded font-mono text-slate-700">
                +{appearance.default_ddi || "55"} {applyMask("11987921030", appearance.phone_mask || "(##) #####-####")}
              </code>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tipo de Negócio Padrão */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-heading text-slate-900 flex items-center gap-2">
            <BarChart2 className="w-5 h-5" style={{ color: tc500 }} />
            Tipo de Negócio Padrão
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 mb-5">
            Define o tipo pré-selecionado ao criar deals e na importação quando o campo não estiver mapeado.
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { value: "recorrente_setup",  label: "Recorrente com Setup",   desc: "MRR + Setup. Ideal para SaaS.",         fields: "mrr \u00b7 setup \u00b7 valor_negocio \u00b7 tcv" },
              { value: "recorrente_produto", label: "Recorrente por Produto", desc: "Qtd \u00d7 valor do produto vinculado.", fields: "produto \u00b7 quantidade \u00b7 valor_negocio" },
              { value: "pontual",           label: "Venda Pontual",          desc: "Valor \u00fanico, sem recorrência.",     fields: "valor_negocio" },
            ].map(opt => {
              const active = (appearance.tipo_negocio_padrao || "recorrente_setup") === opt.value;
              return (
                <button key={opt.value} type="button"
                  data-testid={`tipo-negocio-${opt.value}`}
                  onClick={() => updateAppearance({ tipo_negocio_padrao: opt.value })}
                  className={`flex flex-col items-start gap-2 p-4 rounded-xl border-2 text-left transition-all ${active ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}>
                  <div className="flex items-center justify-between w-full">
                    <span className={`text-sm font-semibold ${active ? "text-blue-700" : "text-slate-800"}`}>{opt.label}</span>
                    {active && <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />}
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">{opt.desc}</p>
                  <code className="text-[10px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded">{opt.fields}</code>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
